/**
 * Speech output with the browser / device voices (Web Speech API).
 * Available voices differ per device; when the preferred one is missing we
 * fall back gracefully instead of failing.
 */
import type { Language } from '@shared/constants';
import type { Settings } from '@shared/schemas';

/** The subset of SpeechSynthesisVoice we rely on (makes testing easy). */
export interface VoiceLike {
  name: string;
  lang: string;
  voiceURI: string;
  default?: boolean;
}

const MALE_HINTS = ['male', 'man', 'masculin', 'homem', 'felipe', 'antonio', 'antônio', 'daniel', 'fred', 'tom', 'oliver', 'george', 'james', 'jorge', 'marcos', 'pedro', 'carlos', 'joão', 'raimundo', 'reed', 'ricardo', 'thiago'];
const FEMALE_HINTS = ['female', 'woman', 'feminin', 'mulher', 'luciana', 'vitoria', 'vitória', 'francisca', 'mônica', 'monica', 'alice', 'samantha', 'karen', 'susan', 'victoria', 'moira', 'fiona', 'tessa', 'zira', 'helena', 'maria', 'joana', 'sara', 'thalita', 'leila', 'raquel'];

export function localeFor(language: Language): string {
  return language === 'pt' ? 'pt-BR' : 'en-US';
}

function normalizeLang(lang: string) {
  return lang.replace('_', '-').toLowerCase();
}

/**
 * Picks the best voice:
 * 1. the voice the user chose (if it exists on this device and matches the language);
 * 2. a voice for the exact locale (pt-BR before pt-PT), matching the gender hint;
 * 3. any voice for the base language; 4. null (the browser default is used).
 */
export function selectVoice<V extends VoiceLike>(
  voices: V[],
  opts: { language: Language; gender: Settings['voiceGender']; voiceURI?: string | null },
): V | null {
  if (!voices.length) return null;
  const locale = localeFor(opts.language).toLowerCase();
  const base = locale.split('-')[0]!;
  const sameBase = (v: V) => normalizeLang(v.lang).split('-')[0] === base;

  if (opts.voiceURI) {
    const chosen = voices.find((v) => v.voiceURI === opts.voiceURI);
    if (chosen && sameBase(chosen)) return chosen;
  }

  const exact = voices.filter((v) => normalizeLang(v.lang) === locale);
  const pool = exact.length ? exact : voices.filter(sameBase);
  if (!pool.length) return null;

  const hints = opts.gender === 'male' ? MALE_HINTS : FEMALE_HINTS;
  const byHint = pool.find((v) => hints.some((h) => v.name.toLowerCase().includes(h)));
  return byHint ?? pool.find((v) => v.default) ?? pool[0]!;
}

export function speechSupported(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window;
}

export function getVoices(): SpeechSynthesisVoice[] {
  return speechSupported() ? window.speechSynthesis.getVoices() : [];
}

/** Calls `cb` whenever the device's voice list changes (it loads asynchronously). */
export function onVoicesChanged(cb: () => void): () => void {
  if (!speechSupported()) return () => {};
  window.speechSynthesis.addEventListener('voiceschanged', cb);
  return () => window.speechSynthesis.removeEventListener('voiceschanged', cb);
}

export function speak(text: string, settings: Settings): boolean {
  const clean = text.trim();
  if (!clean || !speechSupported()) return false;
  const synth = window.speechSynthesis;
  synth.cancel();
  const utterance = new SpeechSynthesisUtterance(clean);
  utterance.lang = localeFor(settings.language);
  utterance.rate = settings.voiceSpeed;
  const voice = selectVoice(getVoices(), {
    language: settings.language,
    gender: settings.voiceGender,
    voiceURI: settings.voiceURI,
  });
  if (voice) utterance.voice = voice;
  // Some devices only have a female voice for pt-BR; a lower pitch gives a
  // more masculine sound when the user asked for a male voice.
  const matchesMale = voice && MALE_HINTS.some((h) => voice.name.toLowerCase().includes(h));
  utterance.pitch = settings.voiceGender === 'male' && !matchesMale ? 0.7 : 1;
  synth.speak(utterance);
  return true;
}

export function stopSpeaking(): void {
  if (speechSupported()) window.speechSynthesis.cancel();
}
